<template>
  <div class="filter-modal-container">
    <client-only>
      <Transition name="slide">
        <div
          v-if="showFiltersMobile"
          class="modal-container is-hidden-tablet"
          role="dialog"
          aria-modal="true"
          :aria-labelledBy="$t('products.filters.title')"
        >
          <Modal
            :is-full="true"
            :title="modalTitle"
            @close="clearAllFiltersAndCloseModal()"
          >
            <template #header="headerProps">
              <div class="column is-full button">
                <button
                  v-if="canShowPreviousFilter"
                  aria-label="back"
                  type="button"
                  @click="showPreviousFilter"
                >
                  <Icons name="arrow" style="--icon-size: 18px" />
                </button>

                <button
                  v-else
                  aria-label="close"
                  type="button"
                  @click="handleClose"
                >
                  <Icons name="close" style="--icon-size: 18px" />
                </button>
              </div>
              <div class="column is-full modal-title">
                {{ headerProps.title }}
              </div>
              <div class="column is-full clear-filter">
                <button v-if="clearFilter.handler" @click="clearFilter.handler">
                  {{ clearFilter.text }}
                </button>
              </div>
            </template>
            <template #default>
              <div class="filters-container">
                <CategoryFiltersContainerMobile
                  v-once
                  :entity-type="entityType"
                />

                <BrandFiltersContainerMobile v-if="entityType !== 'brand'" />

                <PriceRangeFilterContainerMobile
                  :is-reset="isResetPriceSlider"
                  @resetted="isResetPriceSlider = false"
                />

                <FilterTypesContainerMobile v-if="filterTypes.length" />

                <OtherFiltersContainerMobile v-if="otherFilters.length" />
              </div>
            </template>
            <template #footer>
              <div class="mobile-filter-actions">
                <Button
                  :label="modalCTAButtonLabel"
                  :type="showViewResultsCTAButton ? 'btn-default' : 'btn-white'"
                  size="is-fullwidth"
                  @click="handleModalCTA"
                />
              </div>
            </template>
          </Modal>
        </div>
      </Transition>
    </client-only>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Button from '@sephora-asia/core-button/Button.vue'
import Icons from '@sephora-asia/atoms-icons/Icons.vue'
import Modal from '@sephora-asia/core-modal/Modal.vue'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import i18n from '../mixins/i18n'
import categoryHelper from '../mixins/categoryHelper'
import queryString from '../mixins/queryString'
import filterTracking from '../mixins/filterTracking'
import productFiltersHelper from '../mixins/productFiltersHelper'
import CategoryFiltersContainerMobile from './CategoryFiltersContainerMobile.vue'
import BrandFiltersContainerMobile from './BrandFiltersContainerMobile.vue'
import PriceRangeFilterContainerMobile from './PriceRangeFilterContainerMobile.vue'
import FilterTypesContainerMobile from './FilterTypesContainerMobile.vue'
import OtherFiltersContainerMobile from './OtherFiltersContainerMobile.vue'

export default {
  name: 'FilterModalContainer',

  components: {
    Button,
    Icons,
    Modal,
    CategoryFiltersContainerMobile,
    BrandFiltersContainerMobile,
    PriceRangeFilterContainerMobile,
    FilterTypesContainerMobile,
    OtherFiltersContainerMobile
  },

  mixins: [
    i18n,
    categoryHelper,
    queryString,
    filterTracking,
    productFiltersHelper
  ],

  props: {
    entityType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isResetPriceSlider: false
    }
  },

  computed: {
    ...mapGetters({
      ancestorCategoryFilterIdsFor:
        'listingPageFilters/ancestorCategoryFilterIdsFor',
      categoryFilterTrail: 'listingPageFilters/categoryFilterTrail',
      currentCategoryFilter: 'listingPageFilters/currentCategoryFilter',
      currentFilterType: 'listingPageFilters/currentFilterType', // Not used by category filter component, it relies on categoryFilterTrail only
      filterParams: 'listingPageFilters/filterParams',
      filterTypes: 'listingPageFilters/typeFilters',
      initialFilterUrl: 'listingPageFilters/initialFilterUrl',
      meta: 'listingPageProducts/meta',
      otherFilters: 'listingPageFilters/otherFilters',
      searchBrand: 'listingPageFilters/searchBrand',
      showFiltersMobile: 'listingPageFilters/showFiltersMobile',
      showViewResultsCTAButton: 'listingPageFilters/showViewResultsCTAButton'
    }),

    currentCategoryFilterSlide() {
      return this.categoryFilterTrail?.slice(-1).pop()
    },

    modalTitle() {
      if (this.currentCategoryFilterSlide) {
        return (
          this.currentCategoryFilterSlide.title ||
          this.currentCategoryFilterSlide.label
        )
      } else if (this.currentFilterType.title) {
        return this.currentFilterType.title
      }

      return this.$t('products.filters.title')
    },

    canShowPreviousFilter() {
      return this.categoryFilterTrail.length || this.currentFilterType.slugUrl
    },

    clearFilter() {
      if (this.isFilterEnabled || !isEmpty(this.currentCategoryFilter)) {
        // If user is on main filter modal slide
        if (
          !this.categoryFilterTrail.length &&
          !this.currentFilterType.slugUrl
        ) {
          return {
            text: this.$t('products.filters.clearAll'),
            handler: this.handleClearAll
          }
        }

        // For non-category filter
        const currentFilterSlideHasSelection =
          this.currentFilterType.slugUrl &&
          this.$route.query[this.currentFilterType.slugUrl]

        // If user is on category filter modal slide which
        // has a selected category or child category
        const currentCategoryFilterSlideHasSelection =
          this.categoryFilterTrail.length &&
          !isEmpty(this.currentCategoryFilter) &&
          (this.currentCategoryFilterSlide.id === 0 ||
            this.currentCategoryFilter.id ===
              this.currentCategoryFilterSlide.id ||
            this.ancestorCategoryFilterIdsFor(
              this.currentCategoryFilter
            ).includes(this.currentCategoryFilterSlide.id))

        if (
          currentFilterSlideHasSelection ||
          currentCategoryFilterSlideHasSelection
        ) {
          return {
            text: this.$t('products.filters.clear'),
            handler: this.handleClear
          }
        }
      }

      return {
        text: ''
      }
    },

    isFilterEnabled() {
      // clearAll button is not visible when only this filter query params are applied.
      const query = this.filterQueryParams(
        'sort',
        'page',
        'view',
        'language',
        'q'
      )

      if (this.filterParams.brands.length || this.searchBrand) {
        return true
      }
      // To check specially on brands does any topCategory(eg: makeup) is selected or not
      if (this.$route.name === 'brand-listing-page' && isEmpty(query)) {
        return this.$route.params.topCategory
      }

      return !isEmpty(query)
    },

    modalCTAButtonLabel() {
      if (this.showViewResultsCTAButton) {
        // Ref: https://kazupon.github.io/vue-i18n/guide/pluralization.html#accessing-the-number-via-the-pre-defined-argument
        return this.$tc(
          'products.filters.viewResultsWithCount',
          this.meta.totalProducts,
          {
            count: this.meta.totalProducts
          }
        )
      } else {
        return this.$t('dictionary.close')
      }
    }
  },

  methods: {
    clearAllFiltersAndCloseModal() {
      this.$emit('close-modal')
      this.handleClearAll()
    },

    showPreviousFilter() {
      if (this.categoryFilterTrail.length) {
        this.$store.commit(
          'listingPageFilters/setListingPageCategoryFilterTrail',
          {
            pop: true
          }
        )
      } else {
        this.$store.commit(
          'listingPageFilters/setListingPageCurrentFilterType',
          {}
        )
      }
    },

    async handleClearAll() {
      this.isResetPriceSlider = true
      this.$store.commit('listingPageFilters/setListingPageSearchBrand', '')

      // Use await as this triggers a product list refresh API call
      // which will otherwise override the route query reset done
      // by $router.push at the end of this method
      await this.$store.commit(
        'listingPageFilters/clearListingPageFilterParams'
      )

      // if View Results CTA Button is already displayed do nothing
      if (!this.showViewResultsCTAButton) {
        this.$store.commit(
          'listingPageFilters/setShowViewResultsCTAButton',
          true
        )
      }

      const routeOptions = {
        query: {
          sort: this.$route.query.sort,
          view: this.$route.query.view
        }
      }

      if (this.entityType === 'search') {
        routeOptions.query.q = this.$route.query.q
      }

      if (this.entityType !== 'category') {
        routeOptions.params = this.unsetCategoryParams()
      }

      this.trackClearFilterEvent()

      // This router.push() might not be needed as handled by storeWatcher in FilterCardsContainer
      // TODO: remove when tested and safe
      this.$router.push(routeOptions)
    },

    handleClear() {
      if (!this.showViewResultsCTAButton) {
        this.$store.commit(
          'listingPageFilters/setShowViewResultsCTAButton',
          true
        )
      }
      this.currentCategoryFilter?.slugUrl && this.categoryFilterTrail.length
        ? this._clearCategoryFilters()
        : this._clearNonCategoryFilters()
    },

    handleClose() {
      const routeOptions = {
        query: this.initialFilterUrl.query,
        params: this.initialFilterUrl.params
      }

      const query = this.filterQueryParams('page', 'view')

      this.$store.commit('listingPageFilters/setListingPageSearchBrand', '')
      // Why commented?
      // This triggers a race condition with API call in handleClearAll() results in overriding handleClose() API calls.
      // this.$store.commit('listingPageFilters/clearListingPageFilterParams')

      // Optimized to save additional API call. When user just opens & closes filter modal.
      if (
        !isEqual(query, routeOptions.query) ||
        !isEqual(this.$route.params, routeOptions.params)
      ) {
        this.$router.replace(routeOptions)
      }

      this.$emit('close-modal')
      this.$store.commit(
        'listingPageFilters/setShowViewResultsCTAButton',
        false
      )
    },

    _clearCategoryFilters() {
      this.$router.push({
        query: this.$route.query,
        params: this.unsetCategoryParams()
      })
    },

    async _clearNonCategoryFilters() {
      let filterParamsSetter = ''
      let resettedFilterValue = []

      // TODO: This switch case breaks the Open/Closed principle of SOLID design and probably needs refactoring
      switch (this.currentFilterType.type) {
        case 'brand':
          filterParamsSetter =
            'listingPageFilters/setListingPageBrandFilterParams'
          break
        case 'other':
          filterParamsSetter =
            'listingPageFilters/setListingPageOtherFilterParams'
          break
        case 'type':
          filterParamsSetter =
            'listingPageFilters/setListingPageTypeFilterParams'
          resettedFilterValue = {
            [this.currentFilterType.slugUrl]: []
          }
      }

      // Use await as this triggers a product list refresh API call
      // which will otherwise override the route query reset done
      // by $router.push at the end of this method
      await this.$store.commit(filterParamsSetter, resettedFilterValue)

      const query = { ...this.$route.query }

      delete query[this.currentFilterType.slugUrl]
      this.$router.push({ query })
    },

    handleModalCTA() {
      this.$emit('close-modal')
      this.$store.commit(
        'listingPageFilters/setShowViewResultsCTAButton',
        false
      )

      // To open main menu always after closing filter modal
      this.$store.commit(
        'listingPageFilters/setListingPageCurrentFilterType',
        {}
      )

      this.$store.commit(
        'listingPageFilters/setListingPageCategoryFilterTrail',
        { clear: true }
      )
    }
  }
}
</script>

<style lang="scss">
.filter-modal-container {
  .slide-leave-active,
  .slide-enter-active {
    transition: 0.3s;
  }

  .slide-enter {
    transform: translate(100%, 0);
  }

  .slide-leave-to {
    transform: translate(100%, 0);
  }

  .filter-value-list {
    background: $white;
    height: calc(100vh - 57px);
    position: fixed;
    top: 57px;
    bottom: 0;
    width: 100%;
    z-index: $zindex-popover;
    overflow-x: hidden;
  }
}
</style>

<style lang="scss" scoped>
::v-deep .modal {
  .modal-header {
    background: $grey-light;
    padding: 15px;

    .column {
      padding: 0;
      padding-top: 5px;
    }

    .button {
      padding: 0;
      line-height: 0;

      button {
        border: none;
        line-height: 0;
        padding: 0;
      }
    }

    .modal-title {
      font-size: 16px;
      font-weight: 700;
      line-height: 18px;
      text-align: center;
      text-transform: uppercase;
      padding: 0;
      width: 100%;
    }

    .clear-filter {
      padding: 0;
      text-align: right;

      button {
        border: none;
        font-size: 14px;
        line-height: 18px;
        padding: 0;
        text-transform: uppercase;
      }
    }
  }

  .modal-body {
    padding: 0 !important;
    overflow-x: hidden !important;
  }
}

.modal-container {
  ::v-deep .modal .modal-body {
    height: calc(100vh - 57px);
  }
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: $zindex-fixed;
  background: $white;
}

.filters-container {
  background: $white;
  height: calc(100vh - 57px);
}

.price-range-filter-container {
  margin-top: 35px;

  ::v-deep .slider {
    margin: auto;
  }
}

::v-deep {
  .form-checkbox-label,
  .form-radio-label {
    font-size: 14px;
    padding-left: 0 !important;
    text-transform: none;
  }

  .text-field {
    input {
      border: 0;
    }
  }

  .mobile-filter-actions {
    position: fixed;
    bottom: 0;
    padding-right: 15px;
    padding-bottom: 15px;
    padding-left: 15px;
    width: 100%;
    z-index: $zindex-popover;
  }
}
</style>
